import type { CSSProperties } from 'react';

import { cn } from '@/shared/lib/utils';
import { ShadcnProgress } from '@/shared/ui/shadcn';

export type MiniProgressTone = 'low' | 'normal' | 'full';

export interface MiniProgressBarProps {
  /** Aktuální hodnota. */
  value: number;
  /** Maximum (100 %). */
  max: number;
  /** Šířka track v px. Default 34. */
  width?: number;
  /** Výška track v px. Default 4. */
  height?: number;
  /** Explicitní tón výplně; pokud není uvedený, odvodí se z poměru value/max. */
  tone?: MiniProgressTone;
  className?: string;
}

const INDICATOR_TONE: Record<MiniProgressTone, string> = {
  low: 'bg-phx-warning',
  normal: 'bg-phx-primary',
  full: 'bg-phx-success',
};

function resolveTone(value: number, max: number): MiniProgressTone {
  if (max > 0 && value >= max) return 'full';
  const ratio = max > 0 ? value / max : 0;
  return ratio < 0.34 ? 'low' : 'normal';
}

/**
 * Mini progress bar postavený nad shadcn <ShadcnProgress>. Drobný indikátor obsazenosti/kapacity s Phoenix tokeny
 * a automatickým tónem (nízká/běžná/plná). Sdílí ho modulová `CapacityBar` v plánovacím modulu.
 * Rozměry jsou na obalovém <span> (DOM), aby dynamický `style` nešel přes komponentu.
 */
export function MiniProgressBar({
  value,
  max,
  width = 34,
  height = 4,
  tone,
  className,
}: MiniProgressBarProps) {
  const pct = max <= 0 ? 0 : Math.min(100, Math.max(0, Math.round((value / max) * 100)));
  const resolvedTone = tone ?? resolveTone(value, max);
  const style: CSSProperties = { width, height };
  return (
    <span className={cn('inline-block shrink-0', className)} style={style}>
      <ShadcnProgress
        value={pct}
        className="bg-phx-surface-3 rounded-phx-xs h-full w-full"
        indicatorClassName={cn('rounded-phx-xs', INDICATOR_TONE[resolvedTone])}
      />
    </span>
  );
}
